import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { LayoutService } from '../../../../core/services/layout.service';
import { S3ImageUploadService } from '../../../../core/services/azure-image-upload.service';
import { EmotionalTone, FamilyMember, FamilySpace } from '../../../../core/models/story.models';

// -- Types --------------------------------------------------------------------

type SharingOption = 'public' | 'private' | 'family';
type TranscriptTab = 'verbatim' | 'narrative';

interface SpReviewNavState {
  videoUrl?:         string | null;
  imageUrl?:         string | null;
  mediaType?:        string;
  schedulePromptId?: string;
  questionId?:       string;
  inviteeId?:        string;
  deliveryId?:       string;
  questionText?:     string;
  promptTitle?:      string;
  videoDuration?:    number;
}

// -- Component ----------------------------------------------------------------

@Component({
  selector: 'app-scheduled-prompt-review',
  templateUrl: './scheduled-prompt-review.html',
  styleUrl: './scheduled-prompt-review.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class ScheduledPromptReview implements OnInit, AfterViewInit, OnDestroy {
  private readonly router        = inject(Router);
  private readonly route         = inject(ActivatedRoute);
  private readonly platformId    = inject(PLATFORM_ID);
  private readonly api           = inject(ApiService);
  private readonly layoutService = inject(LayoutService);
  private readonly s3Upload      = inject(S3ImageUploadService);
  private readonly zone          = inject(NgZone);
  private readonly cdr           = inject(ChangeDetectorRef);

  // -- View references -------------------------------------------------------

  readonly videoRef           = viewChild<ElementRef<HTMLVideoElement>>('reviewVideo');
  readonly trimCanvas         = viewChild<ElementRef<HTMLCanvasElement>>('trimCanvas');
  readonly trimContainer      = viewChild<ElementRef<HTMLDivElement>>('trimContainer');
  readonly audioRef           = viewChild<ElementRef<HTMLAudioElement>>('reviewAudio');
  readonly audioTrimContainer = viewChild<ElementRef<HTMLDivElement>>('audioTrimContainer');

  // -- Prompt context (from recording link) ----------------------------------

  readonly schedulePromptId = signal('');
  readonly questionId       = signal('');
  readonly inviteeId        = signal('');
  readonly deliveryId       = signal('');
  readonly questionText     = signal('');
  readonly promptTitle      = signal('');

  // -- Video & media ---------------------------------------------------------

  readonly videoUrl   = signal<string | null>(null);
  readonly imageUrl   = signal<string | null>(null);
  readonly mediaType  = signal<string>('video');
  readonly storyTitle = signal('');

  readonly showReloadWarning  = signal(false);
  readonly showPublishConfirm = signal(false);
  readonly isPublishing       = signal(false);
  readonly isTrimmingMedia    = signal(false);
  readonly trimmingProgress   = signal(0);
  readonly titleError         = signal(false);
  readonly publishError       = signal<string | null>(null);
  readonly reviewVideoDuration = signal<number>(0);

  // -- Tagging & sharing -----------------------------------------------------

  readonly familySpaces            = signal<FamilySpace[]>([]);
  readonly familyMembers           = signal<FamilyMember[]>([]);
  readonly selectedFamilySpaceId   = signal<string>('all');
  readonly selectedTaggedMemberIds = signal<string[]>([]);
  readonly sharingOption           = signal<SharingOption>('family');
  readonly selectedSharedMemberIds = signal<string[]>([]);
  readonly memberImageErrors       = signal<Record<string, boolean>>({});
  readonly selectedSharingFamilyId = signal<string>('all');

  readonly filteredFamilyMembers = computed(() => {
    const familyId = this.selectedFamilySpaceId();
    const members  = this.familyMembers();
    return familyId === 'all' ? members : members.filter(m => m.familySpaceId === familyId);
  });

  readonly filteredSharingMembers = computed(() => {
    const familyId = this.selectedSharingFamilyId();
    const members  = this.familyMembers();
    return familyId === 'all' ? members : members.filter(m => m.familySpaceId === familyId);
  });

  // -- Story details ---------------------------------------------------------

  readonly storyDate      = signal(new Date().toISOString().split('T')[0]);
  readonly storyDateError = signal<string | null>(null);
  readonly storyLocation  = signal('');
  readonly emotionalToneId = signal<number>(0);
  readonly emotionalTones  = signal<EmotionalTone[]>([]);

  // -- Custom date picker ----------------------------------------------------

  private static readonly MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  readonly showDatePicker  = signal(false);
  readonly pickerDropUp    = signal(false);
  readonly pickerViewYear  = signal(new Date().getFullYear());
  readonly pickerViewMonth = signal(new Date().getMonth());

  readonly pickerMonthLabel = computed(() =>
    `${ScheduledPromptReview.MONTH_NAMES[this.pickerViewMonth()]} ${this.pickerViewYear()}`,
  );

  readonly storyDateDisplay = computed(() => {
    const val = this.storyDate();
    if (!val) return '';
    const parts = val.split('-');
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d) return val;
    return `${ScheduledPromptReview.MONTH_NAMES[m - 1]} ${d}, ${y}`;
  });

  readonly pickerCalendarDays = computed(() => {
    const year      = this.pickerViewYear();
    const month     = this.pickerViewMonth();
    const firstDay  = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const today     = new Date(); today.setHours(0, 0, 0, 0);
    const selVal    = this.storyDate();
    const selected  = selVal ? new Date(selVal + 'T00:00:00') : null;

    type DayCell = { date: Date | null; day: number | null; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean; isDisabled: boolean };
    const cells: DayCell[] = [];
    for (let i = 0; i < firstDay; i++) {
      cells.push({ date: null, day: null, isCurrentMonth: false, isToday: false, isSelected: false, isDisabled: true });
    }
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(year, month, d); date.setHours(0, 0, 0, 0);
      cells.push({ date, day: d, isCurrentMonth: true, isToday: date.getTime() === today.getTime(), isSelected: selected ? date.getTime() === selected.getTime() : false, isDisabled: date > today });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ date: null, day: null, isCurrentMonth: false, isToday: false, isSelected: false, isDisabled: true });
    }
    return cells;
  });

  readonly pickerCanGoNext = computed(() => {
    const now = new Date();
    return !(this.pickerViewYear() === now.getFullYear() && this.pickerViewMonth() >= now.getMonth());
  });

  openDatePicker(event: MouseEvent): void {
    const val = this.storyDate();
    if (val) { const [y, m] = val.split('-').map(Number); this.pickerViewYear.set(y); this.pickerViewMonth.set(m - 1); }
    const trigger       = event.currentTarget as HTMLElement;
    const rect          = trigger.getBoundingClientRect();
    const spaceBelow    = window.innerHeight - rect.bottom;
    const dropdownHeight = 320;
    this.pickerDropUp.set(spaceBelow < dropdownHeight && rect.top > dropdownHeight);
    this.showDatePicker.set(true);
    this.cdr.markForCheck();
  }

  closeDatePicker(): void { this.showDatePicker.set(false); this.cdr.markForCheck(); }

  selectPickerDate(date: Date | null): void {
    if (!date) return;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (date > today) return;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const value = `${y}-${m}-${d}`;
    this.storyDate.set(value);
    this.validateStoryDate(value);
    this.showDatePicker.set(false);
    this.cdr.markForCheck();
  }

  pickerPrevMonth(): void {
    if (this.pickerViewMonth() === 0) { this.pickerViewMonth.set(11); this.pickerViewYear.update(y => y - 1); }
    else { this.pickerViewMonth.update(v => v - 1); }
  }

  pickerNextMonth(): void {
    if (!this.pickerCanGoNext()) return;
    if (this.pickerViewMonth() === 11) { this.pickerViewMonth.set(0); this.pickerViewYear.update(y => y + 1); }
    else { this.pickerViewMonth.update(v => v + 1); }
  }

  private validateStoryDate(value: string): void {
    if (!value) { this.storyDateError.set('Please enter a valid date.'); return; }
    const year        = parseInt(value.split('-')[0], 10);
    const currentYear = new Date().getFullYear();
    if (isNaN(year) || year < 1000 || year > currentYear) {
      this.storyDateError.set(year > currentYear
        ? `Year cannot be in the future. Please enter a year up to ${currentYear}.`
        : 'Please enter a valid year (1000 or later).');
    } else {
      this.storyDateError.set(null);
    }
  }

  // -- Transcript (hidden but preserved for payload) -------------------------

  readonly transcriptTab       = signal<TranscriptTab>('verbatim');
  readonly transcriptEditMode  = signal(false);
  readonly transcriptVerbatim  = signal('');
  readonly transcriptNarrative = signal('');

  readonly activeTranscriptText = computed(() =>
    this.transcriptTab() === 'verbatim' ? this.transcriptVerbatim() : this.transcriptNarrative(),
  );

  // -- Trim (video) ----------------------------------------------------------

  readonly trimStart     = signal(0);
  readonly trimEnd       = signal(0);
  readonly videoDuration = signal(0);
  readonly trimApplied   = signal(false);

  readonly trimStartPercent      = computed(() => this.videoDuration() > 0 ? (this.trimStart() / this.videoDuration()) * 100 : 0);
  readonly trimEndPercent        = computed(() => this.videoDuration() > 0 ? (this.trimEnd()   / this.videoDuration()) * 100 : 100);
  readonly trimSelectionWidth    = computed(() => this.trimEndPercent() - this.trimStartPercent());
  readonly trimEndExcludePercent = computed(() => 100 - this.trimEndPercent());
  readonly trimmedDuration       = computed(() => Math.max(0, this.trimEnd()       - this.trimStart()));
  readonly removedDuration       = computed(() => Math.max(0, this.videoDuration() - this.trimmedDuration()));

  private draggingHandle: 'start' | 'end' | null = null;
  private readonly boundDragMove = this.onDragMove.bind(this);
  private readonly boundDragEnd  = this.onDragEnd.bind(this);

  // -- Trim (audio) ----------------------------------------------------------

  readonly audioTrimStart   = signal(0);
  readonly audioTrimEnd     = signal(0);
  readonly audioDuration    = signal(0);
  readonly audioTrimApplied = signal(false);

  readonly audioTrimStartPercent      = computed(() => this.audioDuration() > 0 ? (this.audioTrimStart() / this.audioDuration()) * 100 : 0);
  readonly audioTrimEndPercent        = computed(() => this.audioDuration() > 0 ? (this.audioTrimEnd()   / this.audioDuration()) * 100 : 100);
  readonly audioTrimSelectionWidth    = computed(() => this.audioTrimEndPercent() - this.audioTrimStartPercent());
  readonly audioTrimEndExcludePercent = computed(() => 100 - this.audioTrimEndPercent());
  readonly audioTrimmedDuration       = computed(() => Math.max(0, this.audioTrimEnd()    - this.audioTrimStart()));
  readonly audioRemovedDuration       = computed(() => Math.max(0, this.audioDuration()   - this.audioTrimmedDuration()));

  private draggingAudioHandle: 'start' | 'end' | null = null;
  private readonly boundAudioDragMove = this.onAudioDragMove.bind(this);
  private readonly boundAudioDragEnd  = this.onAudioDragEnd.bind(this);

  // -- Thumbnail -------------------------------------------------------------

  readonly thumbnailPreviewUrl = signal<string | null>(null);
  readonly thumbnailFileName   = signal<string | null>(null);
  readonly thumbnailFileSize   = signal<string>('');
  readonly thumbnailError      = signal(false);
  readonly thumbnailTypeError  = signal(false);
  readonly thumbnailDragOver   = signal(false);
  private thumbnailFile: File | null = null;

  // -- Audio playback --------------------------------------------------------

  readonly isAudioPlaying = signal(false);

  // -- Lifecycle -------------------------------------------------------------

  private readonly onBeforeUnload = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const isRefresh = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key === 'r');
    if (isRefresh) { e.preventDefault(); this.showReloadWarning.set(true); }
  };

  async ngOnInit(): Promise<void> {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(true);

    const routeDeliveryId = this.route.snapshot.paramMap.get('deliveryId') ?? '';

    // Guard against direct page refresh — navigate back to record step
    if (isPlatformBrowser(this.platformId)) {
      const wasNavigated = sessionStorage.getItem('fm_sp_navigated');
      sessionStorage.removeItem('fm_sp_navigated');
      if (!wasNavigated) {
        this.redirectToRecord(routeDeliveryId);
        return;
      }
    }

    const navState = (this.router.getCurrentNavigation()?.extras.state ?? history.state ?? {}) as SpReviewNavState;

    // Restore scheduled-prompt context
    const spId     = navState.schedulePromptId || (isPlatformBrowser(this.platformId) ? sessionStorage.getItem('fm_sp_schedule_prompt_id') ?? '' : '');
    const qId      = navState.questionId       || (isPlatformBrowser(this.platformId) ? sessionStorage.getItem('fm_sp_question_id')        ?? '' : '');
    const inv      = navState.inviteeId        || (isPlatformBrowser(this.platformId) ? sessionStorage.getItem('fm_sp_invitee_id')         ?? '' : '');
    const dId      = navState.deliveryId       || routeDeliveryId || (isPlatformBrowser(this.platformId) ? sessionStorage.getItem('fm_sp_delivery_id') ?? '' : '');
    const qText    = navState.questionText     || (isPlatformBrowser(this.platformId) ? sessionStorage.getItem('fm_sp_question_text')      ?? '' : '');
    const pTitle   = navState.promptTitle      || (isPlatformBrowser(this.platformId) ? sessionStorage.getItem('fm_sp_prompt_title')       ?? '' : '');

    this.schedulePromptId.set(spId);
    this.questionId.set(qId);
    this.inviteeId.set(inv);
    this.deliveryId.set(dId);
    this.questionText.set(qText);
    this.promptTitle.set(pTitle);

    if (navState.mediaType)    this.mediaType.set(navState.mediaType);
    if (navState.videoDuration) this.reviewVideoDuration.set(navState.videoDuration);

    const urlFromState = navState.videoUrl ?? null;
    if (navState.imageUrl) this.imageUrl.set(navState.imageUrl);

    // Try sessionStorage fallback for imageUrl
    if (!this.imageUrl() && isPlatformBrowser(this.platformId)) {
      const cached = sessionStorage.getItem('fm_sp_review_image_url');
      if (cached) this.imageUrl.set(cached);
    }

    if (!navState.mediaType && isPlatformBrowser(this.platformId)) {
      const mt = sessionStorage.getItem('fm_sp_media_type');
      if (mt) this.mediaType.set(mt);
    }

    if (urlFromState) {
      this.videoUrl.set(urlFromState);
      if (isPlatformBrowser(this.platformId)) sessionStorage.setItem('fm_sp_review_video_url', urlFromState);
    } else if (isPlatformBrowser(this.platformId)) {
      const cached = sessionStorage.getItem('fm_sp_review_video_url');
      if (!cached) { this.redirectToRecord(dId); return; }
      this.videoUrl.set(cached);
    }

    await this.loadTaggingData();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        const video = this.videoRef()?.nativeElement;
        if (video && video.readyState >= 1) this.resolveDuration(video);
      }, 200);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(false);
    this.onDragEnd();
    this.onAudioDragEnd();
  }

  private redirectToRecord(deliveryId: string): void {
    const id = deliveryId || this.deliveryId();
    if (id) {
      this.router.navigate(['/app/scheduleprompt/record', id], { replaceUrl: true });
    } else {
      this.router.navigate(['/app/scheduleprompt'], { replaceUrl: true });
    }
  }

  // -- Video metadata & trim init --------------------------------------------

  onVideoMetadataLoaded(): void {
    const video = this.videoRef()?.nativeElement;
    if (video) this.resolveDuration(video);
  }

  onVideoTimeUpdate(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || !this.trimApplied()) return;
    if (video.currentTime < this.trimStart()) video.currentTime = this.trimStart();
    else if (video.currentTime >= this.trimEnd()) { video.pause(); video.currentTime = this.trimStart(); }
  }

  onVideoSeeked(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || !this.trimApplied()) return;
    if (video.currentTime < this.trimStart()) video.currentTime = this.trimStart();
    else if (video.currentTime > this.trimEnd()) video.currentTime = this.trimEnd();
  }

  private resolveDuration(video: HTMLVideoElement): void {
    if (isFinite(video.duration) && video.duration > 0) { this.initTrimFromDuration(video.duration); return; }
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      const real = video.currentTime; video.currentTime = 0;
      if (real > 0) this.initTrimFromDuration(real);
    };
    video.addEventListener('seeked', onSeeked);
    video.currentTime = 1e101;
  }

  private initTrimFromDuration(rawDuration: number): void {
    if (!isFinite(rawDuration) || rawDuration <= 0) return;
    const duration = parseFloat(rawDuration.toFixed(3));
    if (this.videoDuration() === duration) return;
    this.videoDuration.set(duration);
    this.trimStart.set(0);
    this.trimEnd.set(duration);
    this.cdr.markForCheck();
    setTimeout(() => this.generateTrimThumbnails(), 50);
  }

  applyTrim(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || this.trimEnd() <= this.trimStart()) return;
    this.trimApplied.set(true);
    video.currentTime = this.trimStart();
    video.play().catch(() => {});
  }

  resetTrim(): void {
    this.trimApplied.set(false);
    this.trimStart.set(0);
    this.trimEnd.set(this.videoDuration());
    const video = this.videoRef()?.nativeElement;
    if (video) { video.pause(); video.currentTime = 0; }
  }

  generateTrimThumbnails(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = this.videoUrl(); const duration = this.videoDuration(); const canvas = this.trimCanvas()?.nativeElement;
    if (!url || !duration || !canvas) return;
    const THUMB_COUNT = 12; const THUMB_H = 56; const THUMB_W = Math.round(THUMB_H * (16 / 9));
    canvas.width = THUMB_COUNT * THUMB_W; canvas.height = THUMB_H;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const vid = document.createElement('video'); vid.src = url; vid.muted = true; vid.preload = 'auto';
    (vid as any).playsInline = true;
    let index = 0;
    const captureNext = () => {
      if (index >= THUMB_COUNT) { this.cdr.markForCheck(); return; }
      vid.currentTime = index === 0 ? 0.01 : (index / (THUMB_COUNT - 1)) * duration;
    };
    vid.addEventListener('seeked',         () => { try { ctx.drawImage(vid, index * THUMB_W, 0, THUMB_W, THUMB_H); } catch (_) {} index++; captureNext(); });
    vid.addEventListener('loadedmetadata', captureNext);
    vid.addEventListener('error',          () => this.cdr.markForCheck());
    vid.load();
  }

  startDragging(handle: 'start' | 'end', event: MouseEvent | TouchEvent): void {
    event.preventDefault(); event.stopPropagation(); this.draggingHandle = handle;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundDragMove);
      document.addEventListener('mouseup',   this.boundDragEnd);
      document.addEventListener('touchmove', this.boundDragMove, { passive: false });
      document.addEventListener('touchend',  this.boundDragEnd);
    });
  }

  onDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.draggingHandle) return;
    const container = this.trimContainer()?.nativeElement; if (!container) return;
    const rect = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const dur = this.videoDuration(); if (!dur) return;
    const time = parseFloat((ratio * dur).toFixed(3));
    this.zone.run(() => {
      if (this.draggingHandle === 'start') this.trimStart.set(Math.min(time, Math.max(0, this.trimEnd() - 0.5)));
      else this.trimEnd.set(Math.max(this.trimStart() + 0.5, Math.min(time, dur)));
      this.trimApplied.set(false); this.cdr.markForCheck();
    });
  }

  onDragEnd(): void {
    this.draggingHandle = null;
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup',   this.boundDragEnd);
    document.removeEventListener('touchmove', this.boundDragMove);
    document.removeEventListener('touchend',  this.boundDragEnd);
  }

  // -- Audio trim ------------------------------------------------------------

  onAudioMetadataLoaded(): void { const a = this.audioRef()?.nativeElement; if (a) this.resolveAudioDuration(a); }

  private resolveAudioDuration(audio: HTMLAudioElement): void {
    if (isFinite(audio.duration) && audio.duration > 0) { this.initAudioTrimFromDuration(audio.duration); return; }
    const onSeeked = () => { audio.removeEventListener('seeked', onSeeked); const r = audio.currentTime; audio.currentTime = 0; if (r > 0) this.initAudioTrimFromDuration(r); };
    audio.addEventListener('seeked', onSeeked); audio.currentTime = 1e101;
  }

  private initAudioTrimFromDuration(rawDuration: number): void {
    if (!isFinite(rawDuration) || rawDuration <= 0) return;
    const duration = parseFloat(rawDuration.toFixed(3));
    if (this.audioDuration() === duration) return;
    this.audioDuration.set(duration); this.audioTrimStart.set(0); this.audioTrimEnd.set(duration); this.cdr.markForCheck();
  }

  applyAudioTrim(): void {
    const audio = this.audioRef()?.nativeElement;
    if (!audio || this.audioTrimEnd() <= this.audioTrimStart()) return;
    this.audioTrimApplied.set(true); audio.currentTime = this.audioTrimStart(); audio.play().catch(() => {});
  }

  resetAudioTrim(): void {
    this.audioTrimApplied.set(false); this.audioTrimStart.set(0); this.audioTrimEnd.set(this.audioDuration());
    const audio = this.audioRef()?.nativeElement; if (audio) { audio.pause(); audio.currentTime = 0; }
  }

  onAudioTimeUpdate(): void {
    const audio = this.audioRef()?.nativeElement; if (!audio || !this.audioTrimApplied()) return;
    if (audio.currentTime < this.audioTrimStart()) audio.currentTime = this.audioTrimStart();
    else if (audio.currentTime >= this.audioTrimEnd()) { audio.pause(); audio.currentTime = this.audioTrimStart(); }
  }

  onAudioPlay():  void { this.isAudioPlaying.set(true);  this.cdr.markForCheck(); }
  onAudioPause(): void { this.isAudioPlaying.set(false); this.cdr.markForCheck(); }

  startAudioDragging(handle: 'start' | 'end', event: MouseEvent | TouchEvent): void {
    event.preventDefault(); event.stopPropagation(); this.draggingAudioHandle = handle;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundAudioDragMove);
      document.addEventListener('mouseup',   this.boundAudioDragEnd);
      document.addEventListener('touchmove', this.boundAudioDragMove, { passive: false });
      document.addEventListener('touchend',  this.boundAudioDragEnd);
    });
  }

  onAudioDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.draggingAudioHandle) return;
    const container = this.audioTrimContainer()?.nativeElement; if (!container) return;
    const rect = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const dur = this.audioDuration(); if (!dur) return;
    const time = parseFloat((ratio * dur).toFixed(3));
    this.zone.run(() => {
      if (this.draggingAudioHandle === 'start') this.audioTrimStart.set(Math.min(time, Math.max(0, this.audioTrimEnd() - 0.5)));
      else this.audioTrimEnd.set(Math.max(this.audioTrimStart() + 0.5, Math.min(time, dur)));
      this.audioTrimApplied.set(false); this.cdr.markForCheck();
    });
  }

  onAudioDragEnd(): void {
    this.draggingAudioHandle = null;
    document.removeEventListener('mousemove', this.boundAudioDragMove);
    document.removeEventListener('mouseup',   this.boundAudioDragEnd);
    document.removeEventListener('touchmove', this.boundAudioDragMove);
    document.removeEventListener('touchend',  this.boundAudioDragEnd);
  }

  // -- Thumbnail -------------------------------------------------------------

  onThumbnailUpload(event: Event): void {
    const input = event.target as HTMLInputElement; const file = input.files?.[0]; if (!file) return; input.value = '';
    this.processThumbnailFile(file);
  }

  removeThumbnail(): void { this.thumbnailFile = null; this.thumbnailPreviewUrl.set(null); this.thumbnailFileName.set(null); this.thumbnailFileSize.set(''); }

  onThumbnailDragOver(event: DragEvent): void  { event.preventDefault(); event.stopPropagation(); this.thumbnailDragOver.set(true); }
  onThumbnailDragLeave(event: DragEvent): void { event.preventDefault(); event.stopPropagation(); this.thumbnailDragOver.set(false); }

  onThumbnailDrop(event: DragEvent): void {
    event.preventDefault(); event.stopPropagation(); this.thumbnailDragOver.set(false);
    const file = event.dataTransfer?.files?.[0]; if (!file) return; this.processThumbnailFile(file);
  }

  private processThumbnailFile(file: File): void {
    if (!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type)) { this.thumbnailTypeError.set(true); return; }
    this.thumbnailTypeError.set(false); this.thumbnailFile = file;
    this.thumbnailFileName.set(file.name); this.thumbnailFileSize.set(`${(file.size / 1024).toFixed(0)} KB`); this.thumbnailError.set(false);
    const reader = new FileReader();
    reader.onload = (e) => { this.thumbnailPreviewUrl.set(e.target?.result as string); this.cdr.markForCheck(); };
    reader.readAsDataURL(file);
  }

  // -- Tagging & sharing -----------------------------------------------------

  selectFamily(familySpaceId: string): void        { this.selectedFamilySpaceId.set(familySpaceId); }
  selectSharingFamily(familySpaceId: string): void  { this.selectedSharingFamilyId.set(familySpaceId); }
  setSharingOption(option: SharingOption): void     { this.sharingOption.set(option); }

  toggleTaggedMember(memberId: string): void {
    this.selectedTaggedMemberIds.update(ids => ids.includes(memberId) ? ids.filter(id => id !== memberId) : [...ids, memberId]);
  }

  toggleSharedMember(memberId: string): void {
    this.selectedSharedMemberIds.update(ids => ids.includes(memberId) ? ids.filter(id => id !== memberId) : [...ids, memberId]);
  }

  isTagged(memberId: string): boolean { return this.selectedTaggedMemberIds().includes(memberId); }
  isShared(memberId: string): boolean { return this.selectedSharedMemberIds().includes(memberId); }

  // -- Member helpers --------------------------------------------------------

  getMemberPhotoUrl(member: FamilyMember): string { return member.image?.trim() || ''; }
  hasMemberImage(member: FamilyMember): boolean   { return !!this.getMemberPhotoUrl(member) && !this.memberImageErrors()[member.familyMemberId]; }
  onMemberImageError(memberId: string): void       { this.memberImageErrors.update(map => ({ ...map, [memberId]: true })); }

  getMemberInitials(member: FamilyMember): string {
    const parts = (member.fullName || '').trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.charAt(0) ?? '') + (parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '')).toUpperCase() || 'NA';
  }

  getFamilyName(member: FamilyMember): string {
    if (member.familyName?.trim()) return member.familyName;
    return this.familySpaces().find(f => f.familySpaceId === member.familySpaceId)?.familyName ?? '';
  }

  // -- Story details ---------------------------------------------------------

  onToneChange(event: Event): void { this.emotionalToneId.set(Number((event.target as HTMLSelectElement).value)); }

  // -- Transcript ------------------------------------------------------------

  setTranscriptTab(tab: TranscriptTab): void { this.transcriptTab.set(tab); }
  toggleTranscriptEdit(): void               { this.transcriptEditMode.update(v => !v); }
  onTranscriptInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (this.transcriptTab() === 'verbatim') this.transcriptVerbatim.set(value);
    else                                     this.transcriptNarrative.set(value);
  }

  // -- Formatting ------------------------------------------------------------

  formatRecordingTime(seconds: number): string {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '00:00';
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  // -- Navigation ------------------------------------------------------------

  backToRecording(): void {
    this.router.navigate(['/app/scheduleprompt/record', this.deliveryId()]);
  }

  dismissReloadWarning(): void { this.showReloadWarning.set(false); }

  confirmReload(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    location.reload();
  }

  // -- Publish ---------------------------------------------------------------

  openPublishConfirm(): void {
    if (!this.storyTitle().trim()) {
      this.titleError.set(true);
      document.getElementById('spTitleInput')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('spTitleInput')?.focus();
      return;
    }
    this.titleError.set(false);

    if (!this.storyDate() || !!this.storyDateError()) {
      if (!this.storyDate()) this.storyDateError.set('Please enter a valid date.');
      return;
    }

    const isAudioType = this.mediaType() === 'audio' || this.mediaType() === 'image-audio';
    if (isAudioType && !this.thumbnailPreviewUrl()) {
      this.thumbnailError.set(true);
      document.getElementById('spReviewThumbnail')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    this.thumbnailError.set(false);
    this.showPublishConfirm.set(true);
  }

  cancelPublish(): void { this.showPublishConfirm.set(false); }

  async saveAndPublish(): Promise<void> {
    this.showPublishConfirm.set(false);
    this.isPublishing.set(true);
    this.publishError.set(null);

    try {
      const isAudioType = this.mediaType() === 'audio' || this.mediaType() === 'image-audio';

      // Client-side trim
      let blobUrl = this.videoUrl() ?? '';
      let trimmedBlobUrl: string | null = null;
      let clientTrimSucceeded = false;

      const hasTrim = isAudioType
        ? (this.audioTrimApplied() && (this.audioTrimStart() > 0.05 || this.audioTrimEnd() < this.audioDuration() - 0.05))
        : (this.trimApplied()      && (this.trimStart()      > 0.05 || this.trimEnd()      < this.videoDuration()  - 0.05));

      if (hasTrim && blobUrl.startsWith('blob:')) {
        const start  = isAudioType ? this.audioTrimStart() : this.trimStart();
        const end    = isAudioType ? this.audioTrimEnd()   : this.trimEnd();
        const elType = isAudioType ? 'audio' : 'video';
        this.isTrimmingMedia.set(true); this.trimmingProgress.set(0); this.cdr.markForCheck();
        try {
          trimmedBlobUrl  = await this.createTrimmedBlob(blobUrl, start, end, elType as 'video' | 'audio');
          blobUrl         = trimmedBlobUrl; clientTrimSucceeded = true;
        } catch { trimmedBlobUrl = null; }
        finally { this.isTrimmingMedia.set(false); this.cdr.markForCheck(); }
      }

      // Upload media
      let mediaFileName = '';
      if (blobUrl.startsWith('blob:')) {
        mediaFileName = await this.s3Upload.uploadStoryMedia(blobUrl, this.mediaType());
      } else {
        mediaFileName = blobUrl.split('/').pop() ?? blobUrl;
      }
      if (trimmedBlobUrl) { URL.revokeObjectURL(trimmedBlobUrl); trimmedBlobUrl = null; }

      // Upload thumbnail
      let thumbnailFileName: string | undefined;
      if (this.thumbnailFile) {
        const thumbBlobUrl = URL.createObjectURL(this.thumbnailFile);
        try { thumbnailFileName = await this.s3Upload.uploadStoryMedia(thumbBlobUrl, this.mediaType()); }
        finally { URL.revokeObjectURL(thumbBlobUrl); }
      }

      const mediaTypeMap: Record<string, number> = { video: 1, audio: 2, 'image-audio': 3 };
      const mediaTypeNum = mediaTypeMap[this.mediaType().toLowerCase()] ?? 1;

      const sharingMap: Record<string, number> = { private: 1, family: 3, public: 3 };
      let visibilityTypeId = sharingMap[this.sharingOption()] ?? 3;
      if (this.sharingOption() === 'family' && this.selectedSharedMemberIds().length > 0) visibilityTypeId = 5;

      const trimmedDur         = isAudioType ? this.audioTrimmedDuration() : this.trimmedDuration();
      const effectiveTrimStart = clientTrimSucceeded ? 0
        : isAudioType ? (this.audioTrimApplied() ? this.audioTrimStart() : 0)
        : (this.trimApplied() ? this.trimStart() : 0);
      const effectiveTrimEnd = clientTrimSucceeded ? trimmedDur
        : isAudioType ? (this.audioTrimApplied() ? this.audioTrimEnd() : (this.audioDuration() || this.reviewVideoDuration()))
        : (this.trimApplied() ? this.trimEnd() : (this.reviewVideoDuration() || this.videoDuration()));

      // TODO: Confirm exact payload with backend team.
      // schedulePromptId, questionId and inviteeId tie this recording to the scheduled prompt.
      const payload = {
        schedulePromptId:      this.schedulePromptId(),
        questionId:            this.questionId(),
        inviteeId:             this.inviteeId(),
        mediaType:             mediaTypeNum,
        title:                 this.storyTitle().trim(),
        mediaFileName,
        thumbnailFileName,
        duration:              clientTrimSucceeded ? trimmedDur : (this.reviewVideoDuration() || (isAudioType ? this.audioDuration() : this.videoDuration())),
        storyDate:             this.storyDate() || new Date().toISOString().split('T')[0],
        location:              this.storyLocation(),
        emotionalToneId:       this.emotionalToneId() || null,
        visibilityTypeId,
        trimStart:             effectiveTrimStart,
        trimEnd:               effectiveTrimEnd,
        taggedFamilyMemberIds: this.selectedTaggedMemberIds(),
        sharedWithUserIds:     this.sharingOption() === 'public' ? [] : this.selectedSharedMemberIds(),
      };

      const res = await this.api.request<{ storyId?: string }>(
        'POST', API_ROUTES.SUBMIT_SCHEDULED_PROMPT_RECORDING, payload,
        { showLoader: true, showToaster: true, useToken: true },
      );

      if (res.status) {
        this.cleanupSessionStorage();
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        window.removeEventListener('keydown', this.onKeyDown);
        this.router.navigate(['/app/scheduleprompt']);
      } else {
        this.publishError.set(res.message ?? 'Failed to submit recording. Please try again.');
        this.isPublishing.set(false);
      }
    } catch {
      this.publishError.set('An unexpected error occurred. Please try again.');
      this.isPublishing.set(false);
    }
  }

  // -- Private helpers -------------------------------------------------------

  private cleanupSessionStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    ['fm_sp_review_video_url','fm_sp_review_image_url','fm_sp_media_type',
     'fm_sp_schedule_prompt_id','fm_sp_question_id','fm_sp_invitee_id',
     'fm_sp_question_text','fm_sp_prompt_title','fm_sp_delivery_id'].forEach(k => sessionStorage.removeItem(k));
  }

  private async createTrimmedBlob(blobUrl: string, start: number, end: number, elementType: 'video' | 'audio'): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) return blobUrl;
    const test = document.createElement(elementType);
    if (typeof (test as any).captureStream !== 'function') return blobUrl;

    const el = document.createElement(elementType) as HTMLVideoElement | HTMLAudioElement;
    el.src = blobUrl; el.volume = 0; (el as any).playsInline = true;
    await new Promise<void>((resolve, reject) => { el.onloadedmetadata = () => resolve(); el.onerror = () => reject(new Error('load failed')); el.load(); });

    const stream: MediaStream = (el as any).captureStream();
    const chunks: Blob[] = [];
    const mimes = elementType === 'video'
      ? ['video/webm;codecs=vp9,opus','video/webm;codecs=vp8,opus','video/webm']
      : ['audio/webm;codecs=opus','audio/webm'];
    const mimeType = mimes.find(m => MediaRecorder.isTypeSupported(m)) ?? '';
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) chunks.push(e.data); };
    const duration = Math.max(0.1, end - start);

    return new Promise<string>((resolve, reject) => {
      recorder.onstop = () => {
        el.src = ''; stream.getTracks().forEach(t => t.stop());
        resolve(URL.createObjectURL(new Blob(chunks, { type: recorder.mimeType || (elementType === 'video' ? 'video/webm' : 'audio/webm') })));
      };
      recorder.onerror = () => reject(new Error('recorder error'));
      el.currentTime = start;
      const onSeeked = () => {
        el.removeEventListener('seeked', onSeeked);
        recorder.start(100); el.play().catch(reject);
        const progressInterval = setInterval(() => {
          const elapsed = Math.max(0, el.currentTime - start);
          this.zone.run(() => { this.trimmingProgress.set(Math.min(99, Math.round((elapsed / duration) * 100))); this.cdr.markForCheck(); });
        }, 200);
        const stopInterval = setInterval(() => {
          if (el.currentTime >= end - 0.05) {
            clearInterval(stopInterval); clearInterval(progressInterval);
            el.pause(); recorder.stop();
            this.zone.run(() => { this.trimmingProgress.set(100); this.cdr.markForCheck(); });
          }
        }, 50);
      };
      el.addEventListener('seeked', onSeeked);
    });
  }

  private async loadTaggingData(): Promise<void> {
    const [familyRes, memberRes, toneRes] = await Promise.all([
      this.api.request<FamilySpace[]>  ('GET', API_ROUTES.GET_ACTIVE_FAMILY_LIST, undefined, { showLoader: false, showToaster: false }),
      this.api.request<FamilyMember[]> ('GET', API_ROUTES.GET_FAMILY_MEMBER_LIST, undefined, { showLoader: false, showToaster: false }),
      this.api.request<EmotionalTone[]>('GET', API_ROUTES.GET_EMOTIONAL_TONES,    undefined, { showLoader: false, showToaster: false }),
    ]);
    if (familyRes.status && familyRes.data) this.familySpaces.set(familyRes.data);
    if (memberRes.status && memberRes.data) {
      this.familyMembers.set(memberRes.data);
      this.selectedSharedMemberIds.set(memberRes.data.slice(0, 3).map(m => m.familyMemberId));
    }
    if (toneRes.status && toneRes.data) this.emotionalTones.set(toneRes.data);
    this.cdr.markForCheck();
  }
}
